import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { SearchInput } from '../../../components';
import { typeaheadPage, useTypeahead } from '../../../hooks/useTypeahead';
import { useUserSearch, type UserSearchFilters } from '../api/identity';
import type { SiteUserSearchResult } from '../types';

function initialsOf(name: string): string {
  return name
    .replace(/[^A-Za-z ]/g, '')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Small square initials avatar (matches the audit `SaCustomerCell` treatment). */
function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: '6px',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'paperAlt',
        border: '1px solid',
        borderColor: 'borderSubtle',
        color: 'text.secondary',
        fontSize: size <= 28 ? 10 : 12,
        fontWeight: 700,
      }}
    >
      {initialsOf(name)}
    </Box>
  );
}

export interface SaUserPickerProps {
  /** The currently-picked user, or null. */
  value: SiteUserSearchResult | null;
  onPick: (user: SiteUserSearchResult) => void;
  onClear: () => void;
  /** Exclude this user id from results (e.g. the other side of a merge). */
  excludeId?: string;
  /**
   * Narrow the candidate set server-side. The impersonation launcher passes
   * `{ activeOnly: true, excludeInternal: true }` because the API rejects those
   * targets; the merge / Entra-link / grant screens legitimately need them and
   * pass nothing (BUG-042).
   */
  filters?: UserSearchFilters;
  placeholder?: string;
  ariaLabel?: string;
  /** Optional per-result trailing node (e.g. a Linked / Internal chip). */
  renderTrailing?: (user: SiteUserSearchResult) => ReactNode;
  /** Optional trailing node on the selected card. */
  selectedTrailing?: (user: SiteUserSearchResult) => ReactNode;
}

/**
 * Cross-customer type-ahead user picker (FE slice 20 §F1/F2/F3). Searches every
 * tenant via `GET /site/users/search` (debounced, ≥2 chars), shows a dropdown of
 * hits, and — once picked — a compact card with a clear affordance. The optional
 * render props let each screen decorate results (Entra link status, internal
 * flag) without forking the component.
 */
export function SaUserPicker({
  value,
  onPick,
  onClear,
  excludeId,
  filters,
  placeholder,
  ariaLabel,
  renderTrailing,
  selectedTrailing,
}: SaUserPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number>();

  const typeahead = useTypeahead();
  const search = useUserSearch(typeahead.searchTerm, 20, filters);
  // Empty while the page on hand answers an older term (BUG-043).
  const page = typeaheadPage(typeahead, search);
  const results = page.items.filter((u) => u.id !== excludeId);
  const hasMore = page.hasMore;
  // Open the list as soon as the term is long enough — including while it is
  // still settling, so the panel shows "searching" instead of nothing (BUG-042).
  const showList = open && typeahead.term.trim().length >= 2;
  const busy = page.loading;

  if (value) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 1.5,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Avatar name={value.customerName ?? value.fullName} size={40} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>
            {value.fullName}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{value.email}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: 'text.secondary' }}>
            {value.customerName ?? t('siteAdmin:identity.internalUser')}
          </Typography>
        </Box>
        {selectedTrailing?.(value)}
        <Box
          component="button"
          type="button"
          onClick={onClear}
          aria-label={t('common:actions.clear', 'Clear')}
          sx={{
            width: 30,
            height: 30,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '7px',
            cursor: 'pointer',
            color: 'text.secondary',
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          ×
        </Box>
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={setAnchorEl} sx={{ position: 'relative' }}>
        <SearchInput
          value={typeahead.term}
          onChange={(v) => {
            typeahead.setTerm(v);
            setOpen(true);
            if (anchorEl) setMenuWidth(anchorEl.offsetWidth);
          }}
          placeholder={placeholder ?? t('siteAdmin:identity.searchUsersPlaceholder')}
          ariaLabel={ariaLabel ?? t('siteAdmin:identity.searchUsersPlaceholder')}
          width="100%"
        />
        <Popper
          open={showList}
          anchorEl={anchorEl}
          placement="bottom-start"
          style={{ zIndex: 1300, width: menuWidth }}
        >
          <Paper sx={{ mt: 0.75, borderRadius: '10px', overflow: 'hidden', boxShadow: 6 }}>
            {busy ? (
              <Box sx={{ p: 2, textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>
                {t('common:states.loading', 'Loading…')}
              </Box>
            ) : results.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>
                {t('siteAdmin:identity.noUsersMatch', { query: typeahead.searchTerm })}
              </Box>
            ) : (
              results.map((u) => (
                <Box
                  key={u.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    onPick(u);
                    setOpen(false);
                    typeahead.reset();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPick(u);
                      setOpen(false);
                      typeahead.reset();
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.375,
                    px: 1.75,
                    py: 1.125,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Avatar name={u.customerName ?? u.fullName} size={28} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                      {u.fullName}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                      {u.email} · {u.customerName ?? t('siteAdmin:identity.internalUser')}
                    </Typography>
                  </Box>
                  {renderTrailing?.(u)}
                </Box>
              ))
            )}
            {!busy && hasMore && (
              <Box
                sx={{
                  px: 1.75,
                  py: 1,
                  fontSize: 11.5,
                  color: 'text.secondary',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {t('common:userPicker.refine')}
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
